import React, { useState, useMemo } from 'react';
import { extractTablesFromText } from '../../utils/tableFormatter';
import { renderTextWithImages } from '../../utils/imageFormatter';
import TableModal from './TableModal';
import './TextWithTables.css';

/**
 * Renders text that may contain pipe-separated markdown tables.
 * Plain text is kept as-is; each table block is replaced by a clickable
 * [Table] badge that opens a styled HTML table in a modal.
 *
 * @param {{
 *   text: string,
 *   className?: string,
 *   preformatted?: boolean,
 *   renderText?: (text: string, key: number) => React.ReactNode
 * }} props
 *
 *   - preformatted: preserves <pre>-like whitespace styling
 *   - renderText: optional custom renderer for text segments (e.g. to also
 *     handle images). Receives the raw text and a React key.
 */
const TextWithTables = ({ text, className, preformatted, renderText }) => {
  const [openIdx, setOpenIdx] = useState(null);

  const segments = useMemo(() => extractTablesFromText(text || ''), [text]);

  const tables = useMemo(
    () => segments.filter(s => s.type === 'table'),
    [segments],
  );

  if (!text) return null;

  const hasAnyTable = tables.length > 0;
  if (!hasAnyTable) {
    if (renderText) return <>{renderText(text, 0)}</>;
    const Tag = preformatted ? 'pre' : 'div';
    const bareCls = [
      preformatted ? 'twt-root' : '',
      className || '',
    ].filter(Boolean).join(' ');
    return <Tag className={bareCls || undefined}>{renderTextWithImages(text, 0)}</Tag>;
  }

  let tableCounter = 0;

  const wrapperCls = [
    'twt-root',
    preformatted ? 'twt-preformatted' : '',
    className || '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={wrapperCls}>
        {segments.map((seg, i) => {
          if (seg.type === 'text') {
            if (renderText) return <React.Fragment key={i}>{renderText(seg.content, i)}</React.Fragment>;
            return <React.Fragment key={i}>{renderTextWithImages(seg.content, i)}</React.Fragment>;
          }

          const tIdx = tableCounter++;
          const caption = seg.tableData?.caption;
          const label = caption ? `Table: ${caption}` : 'Table';
          return (
            <button
              key={i}
              className="twt-table-badge"
              title="Click to view table"
              onClick={() => setOpenIdx(tIdx)}
            >
              {label}
            </button>
          );
        })}
      </div>

      {openIdx !== null && tables[openIdx] && (
        <TableModal
          isOpen
          onClose={() => setOpenIdx(null)}
          headers={tables[openIdx].tableData?.headers || []}
          rows={tables[openIdx].tableData?.rows || []}
          caption={tables[openIdx].tableData?.caption || ''}
        />
      )}
    </>
  );
};

export default TextWithTables;
